import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAgencies, createAgency, updateAgency, deleteAgency, approveAgency, bulkImportAgencies, saveBulkAgencies, getSalesPersons, assignSalesPersonToAgency, getAgencyRequests, approveAgencyRequest, rejectAgencyRequest, api } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Trash, PencilSimple, CheckCircle, UploadSimple, FileXls, Warning, ArrowRight, UserCircle, Clock, XCircle, Buildings, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

const IMPORT_STEPS = { UPLOAD: 'upload', PREVIEW: 'preview', DONE: 'done' };

export default function AgencyPage() {
  const { user } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [formData, setFormData] = useState({ name: '', rate: '', convenience_fee: '', oil_company: '' });
  const [dupeSuggestions, setDupeSuggestions] = useState([]);   // [{ id, name, rate, status }]
  const [dupeCheckLoading, setDupeCheckLoading] = useState(false);

  // Bulk import state
  const [importOpen, setImportOpen] = useState(false);
  const [importStep, setImportStep] = useState(IMPORT_STEPS.UPLOAD);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('skip');
  const [importPreview, setImportPreview] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Sales Person & Approval state
  const [salesPersons, setSalesPersons] = useState([]);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveAgencyId, setApproveAgencyId] = useState(null);
  const [approveAgencyName, setApproveAgencyName] = useState('');
  const [selectedSpId, setSelectedSpId] = useState('');
  const [approving, setApproving] = useState(false);

  // Assign SP state (for active agencies)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignAgency, setAssignAgency] = useState(null);
  const [assignSpId, setAssignSpId] = useState('');

  // Agency Requests from Distributors
  const [agencyRequests, setAgencyRequests] = useState([]);
  const [rejectReason, setRejectReason] = useState('');

  // Search filter
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [agencyRes, spRes, reqRes] = await Promise.all([
        getAgencies(),
        getSalesPersons({ status: 'approved' }),
        getAgencyRequests().catch(() => ({ data: [] })),
      ]);
      setAgencies(agencyRes.data);
      setSalesPersons(spRes.data);
      setAgencyRequests((reqRes.data || []).filter(r => r.status === 'pending'));
    } catch (error) {
      toast.error('Failed to fetch agencies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Debounced duplicate agency-name suggestion (only while creating, not editing) ───
  useEffect(() => {
    if (editingAgency || !dialogOpen) { setDupeSuggestions([]); return undefined; }
    const q = (formData.name || '').trim();
    if (q.length < 2) { setDupeSuggestions([]); return undefined; }
    let cancelled = false;
    setDupeCheckLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/agencies/search', { params: { q } });
        if (!cancelled) setDupeSuggestions(Array.isArray(r.data) ? r.data.slice(0, 5) : []);
      } catch {
        if (!cancelled) setDupeSuggestions([]);
      } finally { if (!cancelled) setDupeCheckLoading(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [formData.name, editingAgency, dialogOpen]);

  const resetForm = () => {
    setFormData({ name: '', rate: '', convenience_fee: '', oil_company: '' });
    setEditingAgency(null);
    setDupeSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Block submit when we have a HIGH-similarity match (95+) that's obviously
    // the same agency, forcing the user to click that suggestion instead.
    const strongDupe = dupeSuggestions.find((s) => (s.score || 0) >= 95);
    if (!editingAgency && strongDupe) {
      toast.error(`"${strongDupe.name}" already exists (${strongDupe.score}% match). Us pe click karo — dubara mat banao.`);
      return;
    }
    try {
      const data = { name: formData.name, rate: parseFloat(formData.rate), convenience_fee: parseFloat(formData.convenience_fee) || 0, oil_company: formData.oil_company || null };
      if (editingAgency) {
        await updateAgency(editingAgency.id, data);
        toast.success('Agency updated');
      } else {
        await createAgency(data);
        toast.success('Agency created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save agency');
    }
  };

  const handleEdit = (agency) => {
    setEditingAgency(agency);
    setFormData({ name: agency.name, rate: agency.rate.toString(), convenience_fee: (agency.convenience_fee || 0).toString(), oil_company: agency.oil_company || '' });
    setDupeSuggestions([]);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this agency?')) return;
    try {
      await deleteAgency(id);
      toast.success('Agency deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete agency');
    }
  };

  const handleApprove = async (id) => {
    // Find agency name
    const agency = agencies.find(a => a.id === id);
    setApproveAgencyId(id);
    setApproveAgencyName(agency?.name || '');
    setSelectedSpId('');
    setApproveOpen(true);
  };

  const confirmApprove = async () => {
    setApproving(true);
    try {
      const payload = selectedSpId ? { sales_person_id: selectedSpId } : {};
      await approveAgency(approveAgencyId, payload);
      toast.success('Agency approved!');
      setApproveOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve agency');
    } finally {
      setApproving(false);
    }
  };

  const handleAssignSp = async () => {
    if (!assignSpId) { toast.error('Sales Person select karo'); return; }
    try {
      await assignSalesPersonToAgency(assignAgency.id, assignSpId);
      toast.success('Sales Person assigned!');
      setAssignOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  // Agency Request handlers (from distributors)
  const handleApproveRequest = async (reqId) => {
    try {
      const res = await approveAgencyRequest(reqId, {});
      toast.success(res.data.message || 'Agency request approved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve failed');
    }
  };

  const handleRejectRequest = async (reqId) => {
    if (!rejectReason.trim()) { toast.error('Reject reason daalo'); return; }
    try {
      const res = await rejectAgencyRequest(reqId, { reason: rejectReason });
      toast.success(res.data.message || 'Agency request rejected');
      setRejectReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject failed');
    }
  };

  // Bulk Import handlers
  const handleFileUpload = async () => {
    if (!importFile) { toast.error('Please select a file'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('mode', importMode);
      const res = await bulkImportAgencies(fd);
      setImportPreview(res.data.preview || []);
      setImportErrors(res.data.errors || []);
      setImportResult(res.data);
      setImportStep(IMPORT_STEPS.PREVIEW);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse file');
    } finally {
      setImporting(false);
    }
  };

  const handleImportSave = async () => {
    setImporting(true);
    try {
      const entriesToSave = importPreview.filter(p => p.action !== 'skip');
      const res = await saveBulkAgencies({ agencies: importMode === 'update' ? importPreview : entriesToSave, mode: importMode });
      setImportResult(res.data);
      setImportStep(IMPORT_STEPS.DONE);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save agencies');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportStep(IMPORT_STEPS.UPLOAD);
    setImportFile(null);
    setImportMode('skip');
    setImportPreview(null);
    setImportErrors([]);
    setImportResult(null);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);

  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAdminOrAbove = isOwner || user?.role === 'assistant';
  const isAdmin = isAdminOrAbove;

  if (!isAdminOrAbove) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-6" data-testid="agency-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Agencies</h1>
        {isAdmin && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { resetImport(); setImportOpen(true); }}
              data-testid="bulk-import-btn"
            >
              <UploadSimple size={18} className="mr-2" /> Import Excel
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) { resetForm(); }
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-agency-btn">
                  <Plus size={18} className="mr-2" /> Add Agency
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingAgency ? 'Edit Agency' : 'Add New Agency'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="form-label">Agency Name</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., HP Gas, Bharat Gas" required data-testid="agency-name-input" />
                    {/* Duplicate-suggestion box — appears live as admin types (create mode only) */}
                    {!editingAgency && (formData.name || '').trim().length >= 2 && (
                      <div className="mt-2">
                        {dupeCheckLoading && dupeSuggestions.length === 0 && (
                          <p className="text-[10px] text-gray-400">Checking for existing agencies…</p>
                        )}
                        {dupeSuggestions.length > 0 && (
                          <div className={`border rounded-lg p-2 ${dupeSuggestions.some(s => (s.score || 0) >= 95) ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`} data-testid="agency-dupe-suggestions">
                            <div className="flex items-center gap-1 mb-1">
                              <Warning size={12} weight="fill" className={dupeSuggestions.some(s => (s.score || 0) >= 95) ? 'text-red-600' : 'text-amber-600'} />
                              <p className={`text-[11px] font-bold ${dupeSuggestions.some(s => (s.score || 0) >= 95) ? 'text-red-800' : 'text-amber-800'}`}>
                                {dupeSuggestions.some(s => (s.score || 0) >= 95) ? 'Ye already exist karta hai — dubara mat banao' : 'Similar agencies pehle se hain — check karo'}
                              </p>
                            </div>
                            <ul className="space-y-1">
                              {dupeSuggestions.map((s) => (
                                <li key={s.id} className="flex items-center justify-between gap-2 bg-white rounded px-2 py-1 border border-gray-200" data-testid={`agency-dupe-${s.id}`}>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-xs font-semibold text-gray-900 truncate">{s.name}</span>
                                    <span className="text-[10px] text-gray-500 ml-2">
                                      ₹{s.rate}
                                      {s.status && s.status !== 'active' ? ` · ${s.status}` : ''}
                                    </span>
                                  </div>
                                  <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${
                                    (s.score || 0) >= 95 ? 'bg-red-600 text-white'
                                    : (s.score || 0) >= 80 ? 'bg-amber-500 text-white'
                                    : 'bg-gray-200 text-gray-700'
                                  }`}>{s.score || '—'}%</span>
                                </li>
                              ))}
                            </ul>
                            <p className="text-[10px] text-gray-500 mt-1 leading-tight">
                              Agar aap different agency bana rahe ho aur naam mile-julte lag rahe hain, to zyada specific likhein (e.g. "GEETA DEVI INDANE — Bihta").
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="form-label">Rate (per unit)</Label>
                    <Input type="number" step="0.01" value={formData.rate} onChange={(e) => setFormData({ ...formData, rate: e.target.value })} placeholder="e.g., 800.00" required data-testid="agency-rate-input" />
                  </div>
                  <div>
                    <Label className="form-label">Convenience Fee / Cylinder</Label>
                    <Input type="number" step="0.01" value={formData.convenience_fee} onChange={(e) => setFormData({ ...formData, convenience_fee: e.target.value })} placeholder="e.g., 25.00" data-testid="agency-conv-fee-input" />
                    <p className="text-xs text-gray-500 mt-1">Per cylinder fixed fee (auto-debit on booking)</p>
                  </div>
                  <div>
                    <Label className="form-label">Oil Company</Label>
                    <select value={formData.oil_company} onChange={(e) => setFormData({ ...formData, oil_company: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="agency-oil-company-select">
                      <option value="">Select Oil Company</option>
                      <option value="Bharat Gas">Bharat Gas</option>
                      <option value="HPCL">HPCL</option>
                      <option value="Indane">Indane</option>
                    </select>
                  </div>
                  <Button type="submit" className="w-full btn-primary" data-testid="agency-submit-btn">
                    {editingAgency ? 'Update Agency' : 'Create Agency'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Agency Requests from Distributors */}
      {agencyRequests.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-lg overflow-hidden" data-testid="agency-requests-section">
          <div className="px-4 py-3 bg-amber-100 border-b border-amber-200 flex items-center gap-2">
            <Clock size={18} className="text-amber-700" weight="fill" />
            <h2 className="font-semibold text-amber-900 text-sm">Pending Agency Requests ({agencyRequests.length})</h2>
            <span className="text-xs text-amber-600 ml-1">Distributors ne ye agencies request ki hain</span>
          </div>
          <div className="divide-y divide-amber-200">
            {agencyRequests.map(req => (
              <div key={req.id || req._id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`agency-req-${req.id || req._id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Buildings size={16} className="text-amber-700" />
                    <p className="font-semibold text-gray-900">{req.agency_name}</p>
                    {req.oil_company && <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded">{req.oil_company}</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Distributor: <strong>{req.distributor_name}</strong>
                    {req.rate ? ` | Rate: ₹${req.rate}` : ''}
                    {req.convenience_fee ? ` | Fee: ₹${req.convenience_fee}/cyl` : ''}
                    {req.note && ` | Note: ${req.note}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                    placeholder="Reject reason..." className="h-8 text-xs w-[150px]" data-testid={`reject-reason-${req.id || req._id}`} />
                  <Button size="sm" className="bg-red-500 hover:bg-red-600 text-xs h-8 px-3"
                    onClick={() => handleRejectRequest(req.id || req._id)} data-testid={`reject-req-${req.id || req._id}`}>
                    <XCircle size={14} className="mr-1" /> Reject
                  </Button>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs h-8 px-3"
                    onClick={() => handleApproveRequest(req.id || req._id)} data-testid={`approve-req-${req.id || req._id}`}>
                    <CheckCircle size={14} className="mr-1" /> Approve
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agency Table */}
      <div className="card-flat overflow-x-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <>
            {/* Search bar */}
            <div className="p-3 border-b bg-gray-50">
              {(() => {
                const q = searchQuery.trim().toLowerCase();
                const filteredCount = q ? agencies.filter((a) =>
                  (a.name || '').toLowerCase().includes(q) ||
                  (a.oil_company || '').toLowerCase().includes(q) ||
                  (a.sales_person_name || '').toLowerCase().includes(q)
                ).length : agencies.length;
                return (
                  <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search agency by name, OMC, sales person…"
                    matchCount={filteredCount}
                    totalCount={agencies.length}
                    noun="agencies (A→Z)"
                    testId="agency-search"
                    inputClassName="pl-8 pr-7 h-8 text-xs"
                  />
                );
              })()}
            </div>
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Agency Name</TableHead>
                  <TableHead>Oil Company</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Fee/Cyl</TableHead>
                  <TableHead>Sales Person</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const q = searchQuery.trim().toLowerCase();
                  const filtered = q
                    ? agencies.filter((a) =>
                        (a.name || '').toLowerCase().includes(q) ||
                        (a.oil_company || '').toLowerCase().includes(q) ||
                        (a.sales_person_name || '').toLowerCase().includes(q)
                      )
                    : agencies;
                  if (filtered.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? 8 : 7} className="text-center text-gray-500">
                          {q ? `Koi agency "${searchQuery}" se match nahi hui` : 'No agencies found'}
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return filtered.map((agency) => (
                  <TableRow key={agency.id} data-testid={`agency-row-${agency.id}`}>
                    <TableCell className="font-medium">{agency.name}</TableCell>
                    <TableCell>{agency.oil_company ? <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{agency.oil_company}</span> : <span className="text-xs text-gray-400">-</span>}</TableCell>
                    <TableCell className="numeric">{formatCurrency(agency.rate)}</TableCell>
                    <TableCell className="numeric">{agency.convenience_fee > 0 ? formatCurrency(agency.convenience_fee) : '-'}</TableCell>
                    <TableCell>
                      {agency.sales_person_name ? (
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded inline-flex items-center gap-1">
                          <UserCircle size={12} /> {agency.sales_person_name}
                        </span>
                      ) : isAdmin ? (
                        <button onClick={() => { setAssignAgency(agency); setAssignSpId(''); setAssignOpen(true); }} className="text-xs text-blue-600 hover:underline" data-testid={`assign-sp-${agency.id}`}>
                          + Assign
                        </button>
                      ) : <span className="text-xs text-gray-400">-</span>}
                    </TableCell>
                    <TableCell>
                      {agency.status === 'pending_approval'
                        ? <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1">Pending</span>
                        : <span className="text-xs bg-green-100 text-green-700 px-2 py-1">Active</span>}
                    </TableCell>
                    <TableCell>{formatDate(agency.created_at)}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        {agency.status === 'pending_approval' && (
                          <Button variant="ghost" size="sm" onClick={() => handleApprove(agency.id)} className="text-green-600 hover:text-green-700 mr-2" data-testid={`approve-agency-${agency.id}`}>
                            <CheckCircle size={18} />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(agency)} className="text-[#002FA7] hover:text-[#001f70] mr-2" data-testid={`edit-agency-${agency.id}`}>
                          <PencilSimple size={18} />
                        </Button>
                        {isOwner && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(agency.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-agency-${agency.id}`}>
                            <Trash size={18} />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </>
        )}
      </div>

      {/* Bulk Import Dialog */}
      <Dialog open={importOpen} onOpenChange={(open) => { setImportOpen(open); if (!open) resetImport(); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileXls size={22} className="text-green-600" />
              Import Agencies from Excel
            </DialogTitle>
          </DialogHeader>

          {/* Step: Upload */}
          {importStep === IMPORT_STEPS.UPLOAD && (
            <div className="space-y-4">
              <div>
                <Label className="form-label">Upload File (.xlsx or .csv)</Label>
                <div className="border-2 border-dashed border-gray-300 p-6 text-center hover:border-[#002FA7] transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.csv"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="hidden"
                    id="import-file-input"
                    data-testid="import-file-input"
                  />
                  <label htmlFor="import-file-input" className="cursor-pointer">
                    {importFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <FileXls size={24} className="text-green-600" />
                        <span className="text-sm font-medium text-gray-900">{importFile.name}</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-500">
                        <UploadSimple size={32} />
                        <span className="text-sm">Click to upload .xlsx or .csv file</span>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 p-3 text-sm text-gray-600">
                <strong>File Format:</strong>
                <div className="mt-1 font-mono text-xs">
                  Column A: Agency Name | Column B: Rate<br />
                  KAUSHIKI BHARATGAS | 918.5<br />
                  RADHIKA GAS SERVICE | 913
                </div>
              </div>

              <div>
                <Label className="form-label">If agency already exists:</Label>
                <div className="flex gap-3 mt-1">
                  <button
                    onClick={() => setImportMode('skip')}
                    className={`flex-1 p-3 border-2 text-sm text-left transition-all ${importMode === 'skip' ? 'border-[#002FA7] bg-blue-50' : 'border-gray-200'}`}
                    data-testid="import-mode-skip"
                  >
                    <div className="font-semibold">Skip</div>
                    <div className="text-xs text-gray-500">Keep existing rate</div>
                  </button>
                  <button
                    onClick={() => setImportMode('update')}
                    className={`flex-1 p-3 border-2 text-sm text-left transition-all ${importMode === 'update' ? 'border-[#002FA7] bg-blue-50' : 'border-gray-200'}`}
                    data-testid="import-mode-update"
                  >
                    <div className="font-semibold">Update Rate</div>
                    <div className="text-xs text-gray-500">Overwrite with new rate</div>
                  </button>
                </div>
              </div>

              <Button
                onClick={handleFileUpload}
                disabled={importing || !importFile}
                className="w-full bg-[#002FA7] hover:bg-[#001f6e] text-white h-11"
                data-testid="import-parse-btn"
              >
                {importing ? 'Parsing...' : 'Parse & Preview'}
                {!importing && <ArrowRight size={18} className="ml-2" />}
              </Button>
            </div>
          )}

          {/* Step: Preview */}
          {importStep === IMPORT_STEPS.PREVIEW && importPreview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="bg-green-50 border border-green-200 px-3 py-2">
                  <span className="text-green-700 font-semibold">{importPreview.filter(p => p.action === 'create').length} New</span>
                </div>
                {importMode === 'update' && (
                  <div className="bg-blue-50 border border-blue-200 px-3 py-2">
                    <span className="text-blue-700 font-semibold">{importPreview.filter(p => p.action === 'update').length} Update</span>
                  </div>
                )}
                <div className="bg-gray-50 border border-gray-200 px-3 py-2">
                  <span className="text-gray-600 font-semibold">{importPreview.filter(p => p.action === 'skip').length} Skip</span>
                </div>
                {importErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 px-3 py-2">
                    <span className="text-red-700 font-semibold">{importErrors.length} Errors</span>
                  </div>
                )}
              </div>

              {/* Errors */}
              {importErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 p-3" data-testid="import-errors">
                  <div className="flex items-center gap-1 text-red-700 font-semibold text-sm mb-1">
                    <Warning size={16} /> Parsing Errors
                  </div>
                  {importErrors.map((err, idx) => (
                    <div key={`err-${err.row}-${idx}`} className="text-sm text-red-600 ml-5">
                      Row {err.row}: {err.name} — {err.error}
                    </div>
                  ))}
                </div>
              )}

              {/* Preview Table */}
              <div className="border border-gray-200 overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-sm" data-testid="import-preview-table">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-semibold text-gray-600">Row</th>
                      <th className="p-2 text-left font-semibold text-gray-600">Agency Name</th>
                      <th className="p-2 text-right font-semibold text-gray-600">Rate</th>
                      <th className="p-2 text-center font-semibold text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.map((p, idx) => (
                      <tr key={`${p.name}-${p.row}`} className={`border-t ${p.action === 'create' ? 'bg-green-50' : p.action === 'update' ? 'bg-blue-50' : ''}`} data-testid={`import-row-${idx}`}>
                        <td className="p-2 text-gray-500">{p.row}</td>
                        <td className="p-2 font-medium">{p.name}</td>
                        <td className="p-2 text-right">
                          {formatCurrency(p.rate)}
                          {p.is_existing && p.existing_rate !== p.rate && (
                            <span className="text-xs text-gray-400 ml-1">(was {formatCurrency(p.existing_rate)})</span>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {p.action === 'create' && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5">New</span>}
                          {p.action === 'update' && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5">Update</span>}
                          {p.action === 'skip' && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5">Skip</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setImportStep(IMPORT_STEPS.UPLOAD)} className="flex-1 h-10" data-testid="import-back-btn">
                  Back
                </Button>
                <Button
                  onClick={handleImportSave}
                  disabled={importing || importPreview.filter(p => p.action !== 'skip').length === 0}
                  className="flex-1 bg-[#002FA7] hover:bg-[#001f6e] text-white h-10"
                  data-testid="import-save-btn"
                >
                  {importing ? 'Importing...' : `Import ${importPreview.filter(p => p.action !== 'skip').length} Agencies`}
                </Button>
              </div>
            </div>
          )}

          {/* Step: Done */}
          {importStep === IMPORT_STEPS.DONE && importResult && (
            <div className="space-y-4 text-center" data-testid="import-success">
              <CheckCircle size={48} weight="fill" className="text-green-500 mx-auto" />
              <h3 className="text-lg font-bold text-gray-900">Import Complete!</h3>
              <p className="text-gray-600">{importResult.message}</p>
              <div className="flex justify-center gap-4 text-sm">
                <span className="text-green-700">{importResult.created} Created</span>
                <span className="text-blue-700">{importResult.updated} Updated</span>
                <span className="text-gray-500">{importResult.skipped} Skipped</span>
              </div>
              <Button onClick={() => { setImportOpen(false); resetImport(); }} className="btn-primary" data-testid="import-done-btn">
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Agency Dialog with Sales Person Selection */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approve Agency</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-gray-600">
              <strong>{approveAgencyName}</strong> ko approve karna hai. Sales Person assign karo (optional):
            </p>
            <div>
              <Label className="form-label">Sales Person</Label>
              <select
                value={selectedSpId}
                onChange={(e) => setSelectedSpId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="approve-sp-select"
              >
                <option value="">-- No Sales Person --</option>
                {salesPersons.map(sp => (
                  <option key={sp.id} value={sp.id}>{sp.name} ({sp.mobile})</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setApproveOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={confirmApprove} disabled={approving} className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-approve-btn">
                {approving ? 'Approving...' : 'Approve'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Sales Person Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Sales Person</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-gray-600">
              <strong>{assignAgency?.name}</strong> ke liye Sales Person select karo:
            </p>
            <div>
              <Label className="form-label">Sales Person</Label>
              <select
                value={assignSpId}
                onChange={(e) => setAssignSpId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="assign-sp-select"
              >
                <option value="">-- Select Sales Person --</option>
                {salesPersons.map(sp => (
                  <option key={sp.id} value={sp.id}>{sp.name} ({sp.mobile})</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setAssignOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleAssignSp} disabled={!assignSpId} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="confirm-assign-sp-btn">
                Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="card-flat">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Note</h3>
        <p className="text-sm text-gray-600">
          Agency rates are automatically used when creating new bookings. Update rates here and they will be applied to future bookings.
        </p>
      </div>
    </div>
  );
}
